import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { NzMessageService } from 'ng-zorro-antd/message';
import { IApiResponseDto, IFilterBody, IPagination, IPostType, ITopic } from '../../../../helpers/general.interface';
import { IGetRedditSubmissionsData } from '../../Models/IRedditPost';
import { IRedditSubmissionDetailsPutDto } from '../../Models/IRedditPostDetailsPutDto';
import { RedditSubmissionDetailsComponent } from '../reddit-post-details/reddit-submission-details.component';
import { faEdit, faEye, faComments, faExternalLink, faSearch, faSave, faCancel, faRemove} from '@fortawesome/free-solid-svg-icons';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { RedditService } from '../../Services/reddit.service';
import { ISubreddit } from '../../Models/IRedditSubreddit';
import { subredditList } from '../../../../constants/constants';
import { IRedditUpdateSubmissionStatusDto } from '../../Models/IRedditUpdateSubmissionStatusDto';

@Component({
  selector: 'app-reddit-submissions',
  templateUrl: './reddit-submissions.component.html',
  styleUrls: ['./reddit-submissions.component.scss']
})
export class RedditSubmissionsComponent implements OnInit {
  filtersForm!: FormGroup;
  politicians: any[] = [];
  attackedPoliticians: any[] = [];
  types: IPostType[] = [];
  topics: ITopic[] = [];

  submissions: IGetRedditSubmissionsData[] = [];
  isDataLoading: boolean = false;
  totalCount: number = 40;
  pageSize: number = 10;
  pageNumber: number = 1;
  pageSizeOptions = [10, 25, 100];
  sortBy: string = 'createdAt'; // Default sort field
  sortDir: string = 'desc'; // Default sort direction
  searchVal: string = ''; // Default search value

  // Filter constants
  allItem = { id: 0, name: 'Te gjitha' };
  unspecifiedItem = { id: -1, name: 'Pa specifikuar' };


  // icons
  faEdit = faEdit;
  faView = faEye; 
  faComments = faComments;
  faExternalLink = faExternalLink;
  faSearch = faSearch;
  faSave = faSave;
  faCancel = faCancel;
  faRemove= faRemove;

  subredditList: ISubreddit[] = subredditList; // List of subreddits

  // Edit cache for inline editing
  editCache: { [key: string]: { edit: boolean; data: IGetRedditSubmissionsData } } = {};

  constructor(
    private formBuilder: FormBuilder,
    private redditService: RedditService,
    private message: NzMessageService,
    private drawerService: NzDrawerService
  ) {}
  

  ngOnInit(): void {
    this.initForm();
    this.getSubreddits();
    this.getPoliticians();
    this.getTopics();
  }
  
  private initForm(): void {
    this.filtersForm = this.formBuilder.group({
      subName: new FormControl('Te gjitha'),
      politician: new FormControl(0),
      topic: new FormControl(0),
      searchBox: new FormControl('')
    });
  }

  // Initialize the edit cache based on submissions data
  updateEditCache(): void {
    this.editCache = {};
    this.submissions.forEach(item => {
      const data = { ...item };
      
      if (data.topicId === null) {
        data.topicId = -1; // Use unspecified value
      }

      if (data.politicianId === null || data.politicianId === undefined) {
        data.politicianId = -1; // Use unspecified value
      }
      
      
      this.editCache[item.id] = {
        edit: false,
        data: data
      };
    });
  }

  startEdit(id: number): void {
    this.editCache[id].edit = true;
  }

  cancelEdit(id: number): void {
    const index = this.submissions.findIndex(item => item.id === id);
    this.editCache[id] = {
      data: { ...this.submissions[index] },
      edit: false
    };
  }

  saveEdit(id: number, politicianId: number, topicId?: number): void {
    const index = this.submissions.findIndex(item => item.id === id);
    
    // Create the DTO object with the required format
    const updateDto: IRedditSubmissionDetailsPutDto = {
      id: id,
      politicianId: politicianId === -1 ? null : politicianId,
      topicId: topicId === -1 ? null : topicId
    };
    
    this.redditService.updateSubmissionDetails(updateDto).subscribe(
      (response: IApiResponseDto) => {
        // Update local data on success
        if (index !== -1) {
          // Update the submission with new values
           
          if (politicianId !== null || politicianId !== undefined) {
            this.submissions[index].politicianId = politicianId;
            const selectedPolitician = this.politicians.find(pol => pol.id === politicianId);
            this.submissions[index].politicianName = selectedPolitician ? selectedPolitician.name : null;
          }
          

          if (topicId === -1) {
            this.submissions[index].topicName = null;
            this.submissions[index].topicId = null;
          } 
          else if (topicId !== undefined) {
            const selectedTopic = this.topics.find(t => t.id === topicId);
            this.submissions[index].topicName = selectedTopic ? selectedTopic.name : null;
          }
        }
        
        this.editCache[id].edit = false;
        this.message.success('Submission updated successfully');
      },
      (error) => {
        this.message.error('Failed to update submission');
        // Revert changes in case of error
        this.cancelEdit(id);
      }
    );
  }

  getAllRedditSubmissions(paginationParams?: IPagination): void {
    this.isDataLoading = true;
    
    // Create pagination object if not provided
    if (!paginationParams) {
      paginationParams = {
        pageNumber: this.pageNumber,
        pageSize: this.pageSize
      };
    }
    
    // Get filter values
    const subName = this.filtersForm.value.subName;
    const politicianId = this.filtersForm.value.politician;
    const topicId = this.filtersForm.value.topic;
    const searchVal = this.filtersForm.value.searchBox;

    // Use populateFilterObj to create the filter body
    const filtersBody = this.populateFilterObj(
      subName,
      politicianId,
      topicId,
      searchVal
    );

    // Call the service method with filters
    this.redditService.getAllSubmissions(
      paginationParams,
      this.sortBy,
      this.sortDir,
      this.searchVal,
      filtersBody
    ).subscribe(
      (response) => {
        this.submissions = response.data;
        this.pageNumber = response.pageNumber;
        this.pageSize = response.pageSize;
        this.totalCount = response.totalElements;
        this.isDataLoading = false;
        this.pageNumber = paginationParams.pageNumber;
        this.pageSize = paginationParams.pageSize;
        
        // Update edit cache after loading submissions
        this.updateEditCache();
      },
      (error) => {
        this.isDataLoading = false;
        this.message.error('Ndodhi një gabim gjatë ngarkimit të të dhënave');
      }
    );
  }

  private populateFilterObj(subName: string, politicianId: number, topicId: number, searchVal: string): IFilterBody[] {
    let filtersBody: IFilterBody[] = [];

    if (subName != null && subName != undefined && subName != "Te gjitha")
         filtersBody.push({ propertyName: 'subName', value: subName.replace('r/', '') });

    if (politicianId != null && politicianId != undefined && politicianId != 0)
       if(politicianId != -1)
          filtersBody.push({ propertyName: 'politicianId', value: politicianId.toString() });
       else
          filtersBody.push({ propertyName: 'politicianId', value: null});
    
    if (topicId != null && topicId != undefined && topicId != 0)
        if(topicId != -1)
           filtersBody.push({ propertyName: 'topicId', value: topicId.toString()});
        else
           filtersBody.push({ propertyName: 'topicId', value: null});
    if (searchVal != null && searchVal.trim() !== '')
      filtersBody.push({ propertyName: 'title', value: searchVal.trim() });

    return filtersBody;
  }

  onQueryParamsChange(params: NzTableQueryParams): void {

    if (params.sort && params.sort.length > 0) {
      const sort = params.sort[0];
      this.sortBy = sort.key;
      this.sortDir = sort.value === 'ascend' ? 'asc' : 'desc';
    }

    let paginationParams: IPagination = {
      pageNumber: params.pageIndex,
      pageSize: params.pageSize
    };
    
    this.getAllRedditSubmissions(paginationParams);
  }

  public search(): void {
    // Reset to first page when searching
    let paginationParams: IPagination = {
      pageNumber: 1,
      pageSize: this.pageSize
    };
    
    this.getAllRedditSubmissions(paginationParams);
  }

  public getPoliticians() {
    this.redditService.getPoliticians().subscribe(
      (data: any) => {
        this.politicians = data;
        this.politicians.unshift(this.allItem, this.unspecifiedItem);
      },
      error => {
        this.message.error('Error loading politicians');
      }
    );
  }
  

  public getTopics() {
    this.redditService.getSubmissionTopics().subscribe(
      (data: any) => {
        this.topics = data;
        this.topics.unshift(this.allItem, this.unspecifiedItem);
      },
      error => {
        this.message.error('Error loading topics');
      }
    );
  }

  public getSubreddits() {
    const subreddits = [...this.subredditList];
    subreddits.unshift({name: "Te gjitha"});
    this.subredditList = subreddits;
  }

  openSubmissionDetails(submissionId: number): void {
    this.drawerService.create<RedditSubmissionDetailsComponent>({
      nzTitle: 'Reddit Submission Details',
      nzContent: RedditSubmissionDetailsComponent,
      nzContentParams: {
        submissionId
      },
      nzWidth: '40%',
      nzClosable: true,
      nzMaskClosable: true
    });
  }


  deactivateSubmission(id: number): void {
    const index = this.submissions.findIndex(item => item.id === id);
   
    const deactivateSubmissionDto: IRedditUpdateSubmissionStatusDto = {
      id: id,
      isActive: false
    };
    
    this.redditService.updateSubmissionStatus(deactivateSubmissionDto).subscribe(
      (response: IApiResponseDto) => {
        this.message.success('Submission deactivated successfully!');
        let paginationParams: IPagination = {
           pageNumber: this.pageNumber,
           pageSize: this.pageSize
        };
        this.getAllRedditSubmissions(paginationParams); //refresh the list after deactivation

      },
      (error) => {
        this.message.error('Failed to deactivate submission');
      }
    );
  }
}